import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  getNextItemNumber,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  GolfPropertyEnum,
  RetailPropertyEnum,
  SpaPropertyEnum,
} from "../../../constants/NavbarList";
import {
  getRetailItemTypeLabel,
  getRetailItemTypeLabels,
  getRetailItemTypeValue,
} from "../../../constants/RetailItemTypes";
import { ProductEnum } from "../../../constants/login";
import { isSampleTestRow } from "../../../excel/mapping/sampleDataFilter";
import {
  validateIntegerLength,
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const withoutSampleRows = (data: unknown): any[] =>
  Array.isArray(data)
    ? data.filter((row) => !isSampleTestRow(row as Record<string, unknown>))
    : [];

const resolveOutletSetup = (fieldValues: any) =>
  fieldValues?.[GolfPropertyEnum.outletSetup] ??
  fieldValues?.[SpaPropertyEnum.outletSetup] ??
  fieldValues?.[RetailPropertyEnum.outletSetup];

const resolveRetailCategories = (fieldValues: any) =>
  fieldValues?.[GolfPropertyEnum.retailCategories] ??
  fieldValues?.[SpaPropertyEnum.retailCategories] ??
  fieldValues?.[RetailPropertyEnum.retailCategories];

const resolveRetailItems = (fieldValues: any) => {
  if (!fieldValues) return [];
  if (Array.isArray(fieldValues)) return withoutSampleRows(fieldValues);
  return withoutSampleRows(
    fieldValues[GolfPropertyEnum.retailItems] ??
      fieldValues[SpaPropertyEnum.retailItems] ??
      fieldValues[RetailPropertyEnum.retailItems] ??
      [],
  );
};

const RetailItemsSetup = ({
  data,
  fieldValues,
  productType = ProductEnum.SPA,
}: {
  data: (value: any) => void;
  fieldValues: any;
  productType?: ProductEnum | string;
}) => {
  const itemTypeLabels = getRetailItemTypeLabels(productType);

  const [outlet, setOutlet] = useState<string | null>(null);
  const [itemNumber, setItemNumber] = useState<string>("");
  const [itemDescription, setItemDescription] = useState<string | null>(null);
  const [salesPrice, setSalesPrice] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("");
  const [category, setCategory] = useState<string | null>(null);
  const [itemType, setItemType] = useState<string | null>(null);

  const [outletErr, setOutletErr] = useState(false);
  const [itemNumberErr, setItemNumberErr] = useState(false);
  const [itemDescriptionErr, setItemDescriptionErr] = useState(false);
  const [salesPriceErr, setSalesPriceErr] = useState(false);
  const [quantityErr, setQuantityErr] = useState(false);
  const [categoryErr, setCategoryErr] = useState(false);
  const [itemTypeErr, setItemTypeErr] = useState(false);

  const [items, setItems] = useState<any[]>([]);
  const [outletList, setOutletList] = useState<string[]>([]);
  const [categoryList, setCategoryList] = useState<string[]>([]);
  const [editClicked, setEditClicked] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");

  useEffect(() => {
    if (!fieldValues) return;
    setItems(resolveRetailItems(fieldValues));
    setOutletList(
      fillAutocompleteValues(
        withoutSampleRows(resolveOutletSetup(fieldValues)),
        DNDFieldsEnum.OUTLETNAME,
        "",
      ),
    );
    setCategoryList(
      fillAutocompleteValues(
        withoutSampleRows(resolveRetailCategories(fieldValues)),
        DNDFieldsEnum.CATEGORYNAME,
        "",
      ),
    );
  }, []);

  useEffect(() => {
    data(items);
    if (!editClicked && selectedId == null) {
      setItemNumber(String(getNextItemNumber(items)));
    }
    editClicked &&
      setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const payload = {
    "Item Number":
      itemNumber.trim() !== "" && !Number.isNaN(Number(itemNumber))
        ? Number(itemNumber)
        : undefined,
    "Item Description": itemDescription,
    Category: category,
    "Sub Category 1": "",
    "Sub Category 2": "",
    "Sub Category 3": "",
    "Sub Category 4": "",
    "Sub Category 5": "",
    "Unit of Measure": "",
    "Sales Price":
      salesPrice.trim() !== "" && !Number.isNaN(Number(salesPrice))
        ? Number(salesPrice)
        : undefined,
    "Bar Code":
      itemNumber.trim() !== "" && !Number.isNaN(Number(itemNumber))
        ? Number(itemNumber)
        : undefined,
    "Secondary Bar Code": "",
    Tax: "",
    "Item Type": getRetailItemTypeValue(itemType, productType),
    "Commission Percent": 0,
    "Commissionable Item": 0,
    "Commission Required": 0,
    "Use Inventory": 0,
    "Grouping Key": 0,
    "Open Items": 0,
    Multipacks: 0,
    Gratuity: 0,
    "Service Charge": 0,
    "Outlet Code": outlet,
    Quantity:
      quantity.trim() !== "" && !Number.isNaN(Number(quantity))
        ? Number(quantity)
        : undefined,
    "Unit Cost": 0,
    "Item Par": 0,
    "Outlet Switch": 0,
    "Allow Earn": 0,
  };

  const clearFieldErrors = () => {
    setOutletErr(false);
    setItemNumberErr(false);
    setItemDescriptionErr(false);
    setSalesPriceErr(false);
    setQuantityErr(false);
    setCategoryErr(false);
    setItemTypeErr(false);
  };

  const validateInput = (): boolean => {
    clearFieldErrors();
    let ok = true;
    let message = "Please correct the highlighted fields";

    if (!outlet || String(outlet).trim() === "") {
      setOutletErr(true);
      ok = false;
      message = "Outlet is mandatory";
    }

    if (itemNumber.trim() === "") {
      setItemNumberErr(true);
      ok = false;
      message = "Item Number is mandatory";
    } else if (
      !validateIntegerLength(
        Number(itemNumber),
        validationLength.postTypeNumber,
      )
    ) {
      setItemNumberErr(true);
      ok = false;
      message = validationMessages.retailRetailItemNumberInvalid;
    }

    if (!itemDescription || String(itemDescription).trim() === "") {
      setItemDescriptionErr(true);
      ok = false;
      message = "Item Description is mandatory";
    } else if (
      !validateString(
        itemDescription,
        validationLength.itemDescription,
        validationType.SPACE,
      )
    ) {
      setItemDescriptionErr(true);
      ok = false;
      message = validationMessages.retailRetailItemDescriptionInvalid;
    }

    if (salesPrice.trim() === "") {
      setSalesPriceErr(true);
      ok = false;
      message = "Sales Price is mandatory";
    } else if (Number.isNaN(Number(salesPrice)) || Number(salesPrice) < 0) {
      setSalesPriceErr(true);
      ok = false;
      message = "Enter a valid sales price";
    }

    if (
      quantity.trim() !== "" &&
      (Number.isNaN(Number(quantity)) ||
        !Number.isInteger(Number(quantity)) ||
        Number(quantity) < 0)
    ) {
      setQuantityErr(true);
      ok = false;
      message = validationMessages.retailRetailItemQuantityInvalid;
    }

    if (!category || String(category).trim() === "") {
      setCategoryErr(true);
      ok = false;
      message = "Category is mandatory";
    }

    if (!itemType || String(itemType).trim() === "") {
      setItemTypeErr(true);
      ok = false;
      message = "Item Type is mandatory";
    }

    if (!ok) {
      setAlertMessage(message);
      setAlertType("error");
      setOpenAlert(true);
    }
    return ok;
  };

  const editItem = (list: any) => {
    if (!list) {
      setSelectedId(null);
      setEditClicked(false);
      setOutlet(null);
      setItemNumber("");
      setItemDescription(null);
      setSalesPrice("");
      setQuantity("");
      setCategory(null);
      setItemType(null);
      clearFieldErrors();
      setItemNumber(String(getNextItemNumber(items)));
      return;
    }
    list?.id && setSelectedId(list.id);
    setEditClicked(true);
    setOutlet(
      list?.["Outlet Code"] ?? list?.Outlet ?? list?.["Outlet Name"] ?? null,
    );
    setItemNumber(
      list["Item Number"] !== undefined && list["Item Number"] !== null
        ? String(list["Item Number"])
        : "",
    );
    setItemDescription(list["Item Description"] ?? null);
    setSalesPrice(
      list["Sales Price"] !== undefined && list["Sales Price"] !== null
        ? String(list["Sales Price"])
        : "",
    );
    setQuantity(
      list.Quantity !== undefined &&
        list.Quantity !== null &&
        String(list.Quantity).trim() !== "" &&
        String(list.Quantity).trim() !== "-"
        ? String(list.Quantity)
        : "",
    );
    setCategory(list?.Category ?? null);
    setItemType(getRetailItemTypeLabel(list["Item Type"], productType));
    clearFieldErrors();
  };

  const updateItem = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        items.filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.ITEMNUMBER,
        Number(itemNumber),
      )
    ) {
      setAlertMessage("Cannot have duplicate item numbers");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setAlertMessage("Retail Items Setup updated successfully");
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) return;
    if (checkDuplicate(items, DNDFieldsEnum.ITEMNUMBER, Number(itemNumber))) {
      setAlertMessage("Cannot have duplicate item numbers");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const displayItems = items.map((item) => ({
    ...item,
    "Item Type":
      getRetailItemTypeLabel(item["Item Type"], productType) ??
      item["Item Type"] ??
      "",
  }));

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Retail Items Setup
          </Typography>
          <Stack direction="row" gap="10px" justifyContent="space-between">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>

        <Stack direction="column" width="100%" gap="20px">
          <Stack>
            <FormLabel>Outlet *</FormLabel>
            <Autocomplete
              disablePortal
              options={removeSampleTestArray(outletList)}
              value={outlet}
              disabled={!outletList?.length}
              onChange={(_e, v) => {
                setOutlet(v);
                setOutletErr(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select outlet"
                  error={outletErr}
                  helperText={outletErr ? validationMessages.fieldRequired : ""}
                />
              )}
            />
          </Stack>

          <Stack>
            <FormLabel>Item Number *</FormLabel>
            <TextField
              variant="outlined"
              placeholder="Item Number"
              value={itemNumber}
              error={itemNumberErr}
              helperText={
                itemNumberErr
                  ? validationMessages.retailRetailItemNumberInvalid
                  : ""
              }
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              onChange={(e) => {
                const value = e.target.value;
                if (value !== "" && !/^\d+$/.test(value)) return;
                setItemNumber(value);
                setItemNumberErr(false);
              }}
              fullWidth
            />
          </Stack>

          <Stack>
            <FormLabel>Item Description *</FormLabel>
            <TextField
              variant="outlined"
              placeholder="Item Description"
              value={itemDescription ?? ""}
              error={itemDescriptionErr}
              helperText={
                itemDescriptionErr
                  ? validationMessages.retailRetailItemDescriptionInvalid
                  : ""
              }
              onChange={(e) => {
                setItemDescription(e.target.value || null);
                setItemDescriptionErr(false);
              }}
              fullWidth
            />
          </Stack>

          <Stack>
            <FormLabel>Sales Price *</FormLabel>
            <TextField
              variant="outlined"
              placeholder="Sales Price"
              value={salesPrice}
              error={salesPriceErr}
              helperText={salesPriceErr ? "Enter a valid sales price" : ""}
              inputProps={{ inputMode: "decimal" }}
              onChange={(e) => {
                const value = e.target.value;
                if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
                setSalesPrice(value);
                setSalesPriceErr(false);
              }}
              fullWidth
            />
          </Stack>

          <Stack>
            <FormLabel>Quantity</FormLabel>
            <TextField
              variant="outlined"
              placeholder="Quantity"
              value={quantity}
              error={quantityErr}
              helperText={
                quantityErr
                  ? validationMessages.retailRetailItemQuantityInvalid
                  : ""
              }
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              onChange={(e) => {
                const value = e.target.value;
                if (value !== "" && !/^\d+$/.test(value)) return;
                setQuantity(value);
                setQuantityErr(false);
              }}
              onWheel={(e) => e.currentTarget.blur()}
              fullWidth
            />
          </Stack>

          <Stack>
            <FormLabel>Category *</FormLabel>
            <Autocomplete
              disablePortal
              options={removeSampleTestArray(categoryList)}
              value={category}
              disabled={!categoryList?.length}
              onChange={(_e, v) => {
                setCategory(v);
                setCategoryErr(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select category"
                  error={categoryErr}
                  helperText={
                    categoryErr ? validationMessages.fieldRequired : ""
                  }
                />
              )}
            />
          </Stack>

          <Stack>
            <FormLabel>Item Type *</FormLabel>
            <Autocomplete
              disablePortal
              options={itemTypeLabels}
              value={itemType}
              onChange={(_e, v) => {
                setItemType(v);
                setItemTypeErr(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select item type"
                  error={itemTypeErr}
                  helperText={
                    itemTypeErr ? validationMessages.fieldRequired : ""
                  }
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction="row" width="100%" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
            Retail Item
          </Button>
        </Stack>

        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Retail Items Available</Typography>
          <Stack direction="column" gap="10px">
            {displayItems?.length ? (
              <DraggableItemList
                itemData={displayItems}
                onOrderUpdate={(order) => {
                  setItems(
                    order.map((row: any) => ({
                      ...row,
                      "Item Type":
                        getRetailItemTypeValue(row["Item Type"], productType) ??
                        row["Item Type"],
                    })),
                  );
                }}
                onEdit={(info) => {
                  const raw = items.find(
                    (item) => String(item.id) === String(info.id),
                  );
                  editItem(raw ?? info);
                }}
                onDelete={(id) => deleteItemById(id)}
                code={DNDFieldsEnum.ITEMNUMBER}
                name={"Item Description"}
              />
            ) : (
              <Typography>No Retail Items Available</Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailItemsSetup;
